import React, { useState, useEffect } from 'react';
import MediaSelector from '../../components/MediaSelector';
import './MenuItemModal.css';

const defaultShowIn = () => ({ delivery: true, pickup: true, eatIn: true });

const normalizeVariations = (variations) => {
  if (!variations || !Array.isArray(variations)) return [];
  return variations.map((v) => ({
    ...v,
    options: v.options || [],
    showIn: v.showIn && typeof v.showIn === 'object'
      ? { delivery: v.showIn.delivery !== false, pickup: v.showIn.pickup !== false, eatIn: v.showIn.eatIn !== false }
      : defaultShowIn()
  }));
};

const MenuItemModal = ({ isOpen, onClose, onSave, item, isProduct = false, categories = [], restaurantId, storeId, variationTemplates = [], variationTemplatesLoading = false }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    price: item?.price || 0,
    image: item?.image || '',
    category: item?.category || '',
    variations: normalizeVariations(item?.variations),
    ingredients: item?.ingredients ? [...item.ingredients] : [],
    stockQty: item?.stockQty != null ? item.stockQty : ''
  });

  // Update formData when item prop changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: item?.name || '',
        description: item?.description || '',
        price: item?.price || 0,
        image: item?.image || '',
        category: item?.category || '',
        variations: normalizeVariations(item?.variations),
        ingredients: item?.ingredients ? [...item.ingredients] : [],
        stockQty: item?.stockQty != null ? item.stockQty : ''
      });
    }
  }, [isOpen, item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      stockQty: formData.stockQty === '' ? null : (formData.stockQty != null ? Number(formData.stockQty) : null)
    };
    onSave(payload);
    onClose();
  };

  const addVariation = () => {
    setFormData({
      ...formData,
      variations: [...formData.variations, {
        name: '',
        type: 'single',
        required: false,
        minSelections: 0,
        maxSelections: 1,
        options: [],
        ...(isProduct && { showIn: defaultShowIn() })
      }]
    });
  };

  const applyTemplate = (template) => {
    if (!template || !Array.isArray(template.variations) || template.variations.length === 0) return;
    const existingNames = new Set(
      (formData.variations || []).map((v) => (v.name || '').trim().toLowerCase()).filter(Boolean)
    );
    const toAdd = template.variations.filter((v) => {
      const name = (v.name || '').trim().toLowerCase();
      return name && !existingNames.has(name);
    });
    if (toAdd.length === 0) return;
    const newVariations = toAdd.map((v) => ({
      name: (v.name || '').trim(),
      type: v.type === 'multiple' ? 'multiple' : 'single',
      required: !!v.required,
      minSelections: v.minSelections ?? 0,
      maxSelections: v.maxSelections ?? (v.type === 'multiple' ? 10 : 1),
      options: Array.isArray(v.options) ? v.options.map((o) => ({ name: (o.name || '').trim(), price: Number(o.price) || 0 })) : [],
      ...(isProduct && { showIn: defaultShowIn() })
    }));
    setFormData({
      ...formData,
      variations: [...formData.variations, ...newVariations]
    });
  };

  const removeVariation = (index) => {
    setFormData({
      ...formData,
      variations: formData.variations.filter((_, i) => i !== index)
    });
  };

  const updateVariation = (index, field, value) => {
    const updated = [...formData.variations];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, variations: updated });
  };

  const addOption = (variationIndex) => {
    const updated = [...formData.variations];
    updated[variationIndex] = {
      ...updated[variationIndex],
      options: [...updated[variationIndex].options, { name: '', price: 0 }]
    };
    setFormData({ ...formData, variations: updated });
  };

  const removeOption = (variationIndex, optionIndex) => {
    const updated = [...formData.variations];
    updated[variationIndex] = {
      ...updated[variationIndex],
      options: updated[variationIndex].options.filter((_, i) => i !== optionIndex)
    };
    setFormData({ ...formData, variations: updated });
  };

  const updateOption = (variationIndex, optionIndex, field, value) => {
    const updated = [...formData.variations];
    updated[variationIndex] = {
      ...updated[variationIndex],
      options: updated[variationIndex].options.map((opt, idx) => 
        idx === optionIndex ? { ...opt, [field]: value } : opt
      )
    };
    setFormData({ ...formData, variations: updated });
  };

  const addIngredient = () => {
    setFormData({ ...formData, ingredients: [...(formData.ingredients || []), ''] });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const updateIngredient = (index, value) => {
    const updated = [...(formData.ingredients || [])];
    updated[index] = value;
    setFormData({ ...formData, ingredients: updated });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{item ? `Edit ${isProduct ? 'Product' : 'Menu Item'}` : `Add ${isProduct ? 'Product' : 'Menu Item'}`}</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Price (€) *</label>
            <input
              type="number"
              step="any"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              required
            />
          </div>
          <div className="form-group">
            <label>Image</label>
            <MediaSelector
              onSelect={(url) => setFormData({ ...formData, image: url })}
              currentValue={formData.image}
              category={isProduct ? "product" : "menu"}
              restaurantId={restaurantId}
              storeId={storeId}
            />
            {formData.image && (
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="Or enter URL manually..."
                style={{ marginTop: '10px' }}
              />
            )}
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="category-select"
            >
              <option value="">Select a category</option>
              {categories.map((cat, index) => (
                <option key={index} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Stock quantity (optional – used when venue has &quot;Track stock&quot; on)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.stockQty === '' ? '' : formData.stockQty}
              onChange={(e) => {
                const v = e.target.value;
                setFormData({ ...formData, stockQty: v === '' ? '' : (parseInt(v, 10) || 0) });
              }}
              placeholder="Leave empty if not using stock"
            />
          </div>

          <div className="ingredients-section">
            <div className="section-header">
              <h3>Ingredients (optional – customer can include/exclude when ordering)</h3>
              <button type="button" onClick={addIngredient} className="btn-add">
                <i className="fas fa-plus"></i>
                Add Ingredient
              </button>
            </div>
            {(formData.ingredients || []).length === 0 && (
              <p className="ingredients-empty">No ingredients. Add ingredients that the customer can choose to include or exclude.</p>
            )}
            {(formData.ingredients || []).map((ing, idx) => (
              <div key={idx} className="ingredient-row">
                <input
                  type="text"
                  placeholder="Ingredient name"
                  value={ing}
                  onChange={(e) => updateIngredient(idx, e.target.value)}
                />
                <button type="button" onClick={() => removeIngredient(idx)} className="btn-remove">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="variations-section">
            <div className="section-header">
              <h3>Variations</h3>
              <div className="section-header-actions">
                {variationTemplatesLoading && (
                  <span className="templates-loading">Loading templates...</span>
                )}
                {!variationTemplatesLoading && variationTemplates && variationTemplates.length > 0 && (
                  <select
                    className="apply-template-select"
                    value=""
                    onChange={(e) => {
                      const id = e.target.value;
                      if (id) {
                        const t = variationTemplates.find((x) => x._id === id);
                        if (t) applyTemplate(t);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Select from template...</option>
                    {variationTemplates.map((t) => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                )}
                <button type="button" onClick={addVariation} className="btn-add">
                  <i className="fas fa-plus"></i>
                  Add Variation
                </button>
              </div>
            </div>
            {formData.variations.length === 0 && (
              <div className="variations-empty-state">
                <i className="fas fa-list-ul"></i>
                <p>No variations yet. Select a template below or add variations manually.</p>
                {variationTemplatesLoading && (
                  <p className="variations-templates-loading">Loading variation templates...</p>
                )}
                {!variationTemplatesLoading && variationTemplates && variationTemplates.length > 0 && (
                  <div className="variations-empty-apply-template">
                    <label htmlFor="empty-state-template-select">Apply variation template:</label>
                    <select
                      id="empty-state-template-select"
                      className="apply-template-select empty-state-select"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id) {
                          const t = variationTemplates.find((x) => x._id === id);
                          if (t) applyTemplate(t);
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">Choose a template...</option>
                      {variationTemplates.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name} ({t.variations?.length ?? 0} variation{(t.variations?.length ?? 0) !== 1 ? 's' : ''})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!variationTemplatesLoading && (!variationTemplates || variationTemplates.length === 0) && (
                  <p className="variations-templates-hint">Create templates in Admin → Variation templates to reuse variations on menu items and products.</p>
                )}
              </div>
            )}
            {formData.variations.map((variation, vIndex) => (
              <div key={vIndex} className="variation-group">
                <div className="variation-header">
                  <input
                    type="text"
                    placeholder="Variation name (e.g., Size, Toppings)"
                    value={variation.name}
                    onChange={(e) => updateVariation(vIndex, 'name', e.target.value)}
                  />
                  <button type="button" onClick={() => removeVariation(vIndex)} className="btn-remove">
                    <i className="fas fa-trash"></i>
                    Remove
                  </button>
                </div>
                <div className="variation-options">
                  <label>
                    <input
                      type="radio"
                      checked={variation.type === 'single'}
                      onChange={() => updateVariation(vIndex, 'type', 'single')}
                    />
                    <i className="fas fa-circle"></i>
                    Single Choice
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={variation.type === 'multiple'}
                      onChange={() => updateVariation(vIndex, 'type', 'multiple')}
                    />
                    <i className="fas fa-check-square"></i>
                    Multiple Choice
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={variation.required}
                      onChange={(e) => updateVariation(vIndex, 'required', e.target.checked)}
                    />
                    <i className="fas fa-asterisk"></i>
                    Required
                  </label>
                </div>
                {isProduct && (
                  <div className="variation-show-in">
                    <span className="show-in-label">Show in orders:</span>
                    <label>
                      <input
                        type="checkbox"
                        checked={variation.showIn?.delivery !== false}
                        onChange={(e) => updateVariation(vIndex, 'showIn', { ...(variation.showIn || defaultShowIn()), delivery: e.target.checked })}
                      />
                      Delivery
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={variation.showIn?.pickup !== false}
                        onChange={(e) => updateVariation(vIndex, 'showIn', { ...(variation.showIn || defaultShowIn()), pickup: e.target.checked })}
                      />
                      Pickup
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={variation.showIn?.eatIn !== false}
                        onChange={(e) => updateVariation(vIndex, 'showIn', { ...(variation.showIn || defaultShowIn()), eatIn: e.target.checked })}
                      />
                      Eat-in
                    </label>
                  </div>
                )}
                <div className="options-list">
                  <button type="button" onClick={() => addOption(vIndex)} className="btn-add-option">
                    <i className="fas fa-plus-circle"></i>
                    Add Option
                  </button>
                  {variation.options.map((option, oIndex) => (
                    <div key={oIndex} className="option-item">
                      <input
                        type="text"
                        placeholder="Option name"
                        value={option.name}
                        onChange={(e) => updateOption(vIndex, oIndex, 'name', e.target.value)}
                      />
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="Price (€)"
                        value={option.price}
                        onChange={(e) => updateOption(vIndex, oIndex, 'price', parseFloat(e.target.value) || 0)}
                      />
                      <button type="button" onClick={() => removeOption(vIndex, oIndex)} className="btn-remove">
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenuItemModal;
